import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import useAuth from "../../../../hooks/useAuth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams } from "react-router-dom";
import Chat from "./components/Chat";
import ChatUsers from "./components/ChatUsers";
import { fetchUserDetails } from "../../../../services/api/user";
import { useDispatch, useSelector } from "react-redux";
import { useChatSocket } from "../../../../hooks/useChatSocket";
import { UserType } from "../../../../utils/enum";
import { getChatUserBlurPredicate, pickDefaultChatUser } from "../../../../utils/helpers";
import { uploadMessageFiles } from "../../../../services/api/message";
import {
  clearWorkOrderDraftMessage,
  setWorkOrderDraftMessage,
} from "../../../../store/workOrder/workOrderSlice";
import { showToast } from "../../../../store/toast/toastSlice";

const getConversationId = (selectedUser, chatRoom) =>
  selectedUser?.conversationId ?? selectedUser?.chatRoomId ?? chatRoom?.id ?? null;

const ChatTab = forwardRef(({ workOrderDetail, isVisible = true, isActive = true }, ref) => {
  const {
    isPending: isUploadMessageFilesMutationApiLoading,
    error: isUploadMessageFilesMutationApiError,
    data: filesData,
    mutateAsync: uploadMessageFilesMutation,
  } = useMutation({
    mutationFn: uploadMessageFiles,
    enabled: false,
  });

  // Read off the router, not window: arriving from a notification while this
  // tab is already open is a navigation, not a reload, so the params have to be
  // re-read on re-render rather than once at mount.
  const { search: locationSearch, key: locationKey } = useLocation();
  const queryParams = new URLSearchParams(locationSearch);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { id: workOrderId, userId } = useParams();
  const engineerId = parseInt(queryParams.get("engineerId"));
  // Message notifications for the work order chat deep-link by room: it has no
  // engineer to name it by.
  const deepLinkedChatRoomId = parseInt(queryParams.get("chatRoomId"));
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const { auth, authRole } = useAuth();
  const [message, setMessage] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const draftMessages = useSelector((state) => state.workOrder.draftMessages[workOrderId]);
  const prevSelectedUserRef = useRef(null);
  // Two separate locks, both needed to stop a held Enter key duplicating a send.
  //
  // isSendingRef  - the upload/send is still in flight.
  // awaitingClearRef - the composer has been cleared but that has not rendered
  //   yet. The key handler closes over the message/files of the render that
  //   created it, so until the empty composer commits, a queued keypress still
  //   sees the old content and would send it again.
  //
  // A send is allowed only when both are clear.
  const isSendingRef = useRef(false);
  const awaitingClearRef = useRef(false);
  const isSendLocked = () => isSendingRef.current || awaitingClearRef.current;
  const hasAutoOpenedForOwner = useRef(false);
  const [replyTo, setReplyTo] = useState(null);
  const {
    unreadCount,
    chatRoom,
    chatUsersList,
    isfetchGroupChatUsersForWorkOrderApiLoading,
    handleJoinGroupChat,
    setChatVisibility,
    handleSendMessage,
    handleEditMessage,
    handleDeleteMessage,
    handleToggleReaction,
    refetchChatUsers,
    messages,
    chatRoomSendState,
    lastUnreadMessageId,
    isMessagesLoading,
    isCompanyOwner,
    loadOlderMessages,
    hasMoreMessages,
    isFetchingOlderMessages,
  } = useChatSocket(workOrderId, workOrderDetail?.companyId, engineerId);
  const [isWindowActive, setIsWindowActive] = useState(true);

  const openChatRoomId = chatRoom?.id ?? null;

  useEffect(() => {
    const report = () =>
      setChatVisibility(Boolean(isVisible && openChatRoomId && !document.hidden), openChatRoomId);

    report();
    document.addEventListener("visibilitychange", report);

    return () => {
      document.removeEventListener("visibilitychange", report);
      // Stop counting as reading this conversation once it is torn down.
      setChatVisibility(false, null);
    };
  }, [isVisible, openChatRoomId, setChatVisibility]);

  useEffect(() => {
    if (isVisible) refetchChatUsers();
  }, [
    isVisible,
    refetchChatUsers,
    workOrderDetail?.engineerId,
    workOrderDetail?.assignedFreelanceCompanyId,
    // The work order activity chat is created the moment the tech confirms, so
    // the list has to be re-read on that transition — otherwise the new thread
    // only appears after a manual page reload.
    workOrderDetail?.subStatus,
    workOrderDetail?.status,
  ]);

  const refetchUserDetailsWithId = async (userId, direct = false) => {
    try {
      const userData = await fetchUserDetails(userId, direct);
      queryClient.setQueryData(["userDetails", userId], userData);
      return userData;
    } catch (error) {
      console.error("Failed to refetch user details:", error);
      throw error;
    }
  };
  useImperativeHandle(ref, () => ({
    saveDraft: () => {
      dispatch(
        setWorkOrderDraftMessage({
          workOrderId,
          userId: selectedUser?.id,
          message,
          files: selectedFiles,
        }),
      );
    },
  }));
  useEffect(() => {
    if (prevSelectedUserRef.current) {
      dispatch(
        setWorkOrderDraftMessage({
          workOrderId,
          userId: prevSelectedUserRef.current.id,
          message,
          files: selectedFiles,
          showEditor,
        }),
      );
    }
    prevSelectedUserRef.current = selectedUser;
    const draft = draftMessages?.[selectedUser?.id];
    if (draft) {
      setMessage(draft.message || "");
      setSelectedFiles(draft.files || []);
      setShowEditor(draft.showEditor || false);
    } else {
      setMessage("");
      setSelectedFiles([]);
      setShowEditor(false);
    }
  }, [selectedUser]);

  useEffect(() => {
    if (draftMessages?.[selectedUser?.id]) {
      setMessage(draftMessages?.[selectedUser?.id].message || "");

      // const filesWithPreview = (draftMessages.files || []).map(f => {
      //   return {
      //     ...f,
      //     previewURL: f.file ? URL.createObjectURL(f.file) : null,
      //   };
      // });
      // setSelectedFiles(filesWithPreview);
      setShowEditor(draftMessages?.[selectedUser?.id].showEditor || false);
    } else {
      setMessage("");
      setSelectedFiles([]);
      setShowEditor(false);
    }
  }, [workOrderId, draftMessages?.[selectedUser?.id]]);

  const seenRoomIdsRef = useRef(new Set());

  useEffect(() => {
    if (!selectedUser || !chatUsersList?.length) return;
    const roomIdOf = (entry) => entry?.chatRoomId ?? entry?.conversationId ?? null;
    chatUsersList.forEach((entry) => {
      const id = roomIdOf(entry);
      if (id) seenRoomIdsRef.current.add(id);
    });

    const openRoomId = chatRoom?.id ?? roomIdOf(selectedUser);
    if (!openRoomId) return;
    if (!seenRoomIdsRef.current.has(openRoomId)) return;
    const stillThere = chatUsersList.some((entry) => roomIdOf(entry) === openRoomId);
    if (!stillThere) setSelectedUser(null);
  }, [chatUsersList, selectedUser, chatRoom?.id]);

  const handledDeepLinkRef = useRef(null);
  // One engineer deep link handled per navigation — see the effect below.
  const handledEngineerLinkRef = useRef(null);
  // Moving between work orders is a param change on a mounted route, not a
  // remount, so nothing here resets by itself: the selection, and with it the
  // joined room the message pane reads, survived into the next work order and
  // showed the previous job's thread. Clearing the selection puts the usual
  // open-a-default / follow-the-deep-link effects back in charge for the new job.
  const prevWorkOrderIdRef = useRef(workOrderId);
  useEffect(() => {
    if (prevWorkOrderIdRef.current === workOrderId) return;
    prevWorkOrderIdRef.current = workOrderId;
    prevSelectedUserRef.current = null;
    hasAutoOpenedForOwner.current = false;
    handledDeepLinkRef.current = null;
    handledEngineerLinkRef.current = null;
    seenRoomIdsRef.current = new Set();
    setSelectedUser(null);
  }, [workOrderId]);

  // The thread a ?chatRoomId= link names, once the list it lives in has loaded.
  const deepLinkedChatEntry = useMemo(() => {
    if (!deepLinkedChatRoomId || !chatUsersList?.length) return null;
    return (
      chatUsersList.find(
        (user) => (user?.chatRoomId ?? user?.conversationId) === deepLinkedChatRoomId,
      ) ?? null
    );
  }, [deepLinkedChatRoomId, chatUsersList]);

  // Opened from a notification that names the room rather than a person.
  //
  // Keyed on the navigation, not on whether a chat is already open: following a
  // second notification from this page is a new navigation to the same route,
  // so the link has to be able to SWITCH threads, not just pick the first one.
  // Re-running per navigation also leaves a thread the user picks by hand alone.
  useEffect(() => {
    if (!deepLinkedChatEntry) return;
    const navigation = `${locationKey}:${deepLinkedChatRoomId}`;
    if (handledDeepLinkRef.current === navigation) return;
    handledDeepLinkRef.current = navigation;
    if (getConversationId(selectedUser, chatRoom) === deepLinkedChatRoomId) return;
    openChat(deepLinkedChatEntry);
  }, [deepLinkedChatEntry, deepLinkedChatRoomId, locationKey]);

  useEffect(() => {
    // A ?chatRoomId= that names no thread in this list (stale link, lost access)
    // must not leave the pane empty — fall through to the usual default.
    if (chatUsersList?.length > 0 && !selectedUser && !engineerId && !deepLinkedChatEntry) {
      // Deleted accounts can't be opened, so they never make a default. Among the
      // rest, prefer the assigned engineer: the customer↔team room now exists from
      // the moment the ticket is raised and, with no messages on either, can sort
      // ahead of the engineer's room. Falls back to the first active user (and to
      // the raw first entry when every user is deleted).
      const activeUsers = chatUsersList.filter((user) => !user?.isDeletedUser);
      openChat(pickDefaultChatUser(activeUsers, workOrderDetail) || chatUsersList[0]);
    }
  }, [chatUsersList, selectedUser, deepLinkedChatEntry]);

  useEffect(() => {
    if (!isActive || !workOrderDetail?.companyId) return;
    if (authRole !== UserType.CLIENT && !isCompanyOwner) return;
    refetchChatUsers();
  }, [isActive, workOrderDetail?.companyId, authRole, isCompanyOwner]);

  useEffect(() => {
    if (!isActive || !workOrderDetail?.companyId) return;
    if (authRole !== "FREELANCER" || isCompanyOwner) return;
    if (chatRoom?.id || !auth?.user?.id) return;
    // Only bootstraps the default thread. Joining clears chatRoom first, so once
    // a tech has picked a chat this must not fire again — it would drag them
    // straight back out of the work order chat they just opened.
    if (selectedUser) return;
    openChat(auth.user);
  }, [isActive, workOrderDetail?.companyId, authRole, isCompanyOwner, chatRoom?.id, selectedUser]);

  useEffect(() => {
    if (authRole === "FREELANCER" && !isCompanyOwner && !selectedUser) {
      openChat(auth?.user);
    }
    // Company owner returning to tab with a cached room — open immediately like a direct engineer
    if (authRole === "FREELANCER" && isCompanyOwner && chatRoom?.id) {
      hasAutoOpenedForOwner.current = true;
      openChat(auth?.user);
    }
  }, [authRole, isCompanyOwner]);

  useEffect(() => {
    if (
      authRole === "FREELANCER" &&
      isCompanyOwner &&
      !isfetchGroupChatUsersForWorkOrderApiLoading &&
      chatUsersList?.length === 0 &&
      !hasAutoOpenedForOwner.current
    ) {
      hasAutoOpenedForOwner.current = true;
      openChat(auth?.user);
    }
  }, [authRole, isCompanyOwner, chatUsersList, isfetchGroupChatUsersForWorkOrderApiLoading]);

  // Keyed on the work order too: the same engineer often staffs the next job, so
  // on `engineerId` alone this never re-ran and the new job kept the old thread.
  useEffect(() => {
    if ((authRole !== "CLIENT" && !isCompanyOwner) || !engineerId) return;
    const navigation = `${locationKey}:${engineerId}`;
    if (handledEngineerLinkRef.current === navigation) return;
    if (selectedUser?.id === engineerId) {
      handledEngineerLinkRef.current = navigation;
      return;
    }

    // Open the engineer's own list row, exactly as clicking it would. Building
    // the thread from fetchUserDetails instead gave the header a different
    // shape from the list, which is why a deep link showed only a first name.
    const entry = chatUsersList?.find((user) => user?.id === engineerId);
    if (entry) {
      handledEngineerLinkRef.current = navigation;
      openChat(entry);
      return;
    }

    // No row yet — an engineer whose room was never created. Fall back to their
    // profile so the join still creates it. Marked handled only once the list
    // has actually loaded, so a link that arrives mid-fetch is still honoured.
    if (isfetchGroupChatUsersForWorkOrderApiLoading) return;
    handledEngineerLinkRef.current = navigation;
    refetchUserDetailsWithId(engineerId, true)
      .then((userData) => openChat(userData))
      .catch((error) => console.error("Failed to refetch user details:", error));
  }, [
    engineerId,
    isCompanyOwner,
    workOrderId,
    chatUsersList,
    selectedUser,
    locationKey,
    isfetchGroupChatUsersForWorkOrderApiLoading,
  ]);

  // Customers have a single engineer-less room with the team. Render the chat panel
  // (select self); the actual room join is handled socket-aware inside useChatSocket.
  useEffect(() => {
    if (authRole === "CUSTOMER" && !hasAutoOpenedForOwner.current) {
      hasAutoOpenedForOwner.current = true;
      setSelectedUser(auth?.user);
    }
  }, [authRole]);

  // Customers have a single engineer-less room with the team. Render the chat panel
  // (select self); the actual room join is handled socket-aware inside useChatSocket.
  useEffect(() => {
    if (authRole === "CUSTOMER" && !hasAutoOpenedForOwner.current) {
      hasAutoOpenedForOwner.current = true;
      setSelectedUser(auth?.user);
    }
  }, [authRole]);

  const isDeletedChatUser = (user) =>
    !!user?.isDeletedUser ||
    !!chatUsersList?.find((entry) => entry?.id === user?.id)?.isDeletedUser ||
    (workOrderDetail?.engineer?.id === user?.id && !!workOrderDetail?.engineer?.isDeletedUser);

  const openChat = async (user) => {
    if (!user) return;
    setSelectedUser(user);
    if (isDeletedChatUser(user)) return;
    // Only the work order chat is opened by id — it shares its counterpart with
    // the tech's own engineer room, so nothing else identifies it. Every other
    // thread keeps resolving from the counterpart exactly as before: that path
    // also creates a missing room and adds the opener as a participant, which
    // the id path (a pure read of an existing room) deliberately does not.
    handleJoinGroupChat(
      workOrderId,
      workOrderDetail?.companyId,
      user.id,
      user?.isWorkOrderChat ? (user.chatRoomId ?? user.conversationId ?? null) : null,
    );
  };

  const handleMessage = (e) => {
    let value = e.target.value;
    setMessage(value);
  };
  const isEmptyHtml = (html = "") => {
    const div = document.createElement("div");
    div.innerHTML = html;

    return div.textContent.replace(/\u00A0/g, "").trim().length === 0;
  };

  const sendMessage = async () => {
    if (isSendLocked()) return;

    const conversationId = getConversationId(selectedUser, chatRoom);
    const isMessageEmpty = isEmptyHtml(message);

    if (isMessageEmpty && selectedFiles.length === 0) return;

    if (!conversationId) return;

    // Take what is being sent, then empty the composer straight away — before
    // the upload, not after it. Waiting meant the box still showed the message
    // while it uploaded, and anything typed in the meantime was wiped when the
    // send finally resolved.
    const messageToSend = isMessageEmpty ? "" : message;
    const filesToSend = selectedFiles;
    const replyToId = replyTo?.id ?? null;

    isSendingRef.current = true;
    awaitingClearRef.current = true;
    setMessage("");
    setReplyTo(null);
    dispatch(clearWorkOrderDraftMessage({ workOrderId, userId: selectedUser?.id }));
    setSelectedFiles([]);

    try {
      let filesResponse = [];
      if (filesToSend.length) {
        const formData = new FormData();
        filesToSend.forEach((file) => formData.append("files", file.file));
        formData.append("workOrderId", workOrderId);
        filesResponse = await uploadMessageFilesMutation(formData);
        filesResponse = filesResponse.data.data;
      }

      handleSendMessage(
        messageToSend,
        conversationId,
        workOrderId,
        selectedUser?.id,
        filesResponse,
        null,
        replyToId,
      );
    } catch (error) {
      console.error("Failed to send message:", error);
      dispatch(
        showToast({ message: "Message could not be sent. Please try again.", type: "error" }),
      );
      // Put the content back, but only if nothing new has been typed since —
      // restoring over a fresh draft would throw away what the user just wrote.
      setMessage((current) => (isEmptyHtml(current) ? messageToSend : current));
      setSelectedFiles((current) => (current.length ? current : filesToSend));
    } finally {
      isSendingRef.current = false;
    }
  };

  // Releases the second lock once the emptied composer has actually rendered.
  // Until this runs, keypresses queued behind the send still see the old
  // content in their closure, and the guard is what stops them re-sending it.
  useEffect(() => {
    if (awaitingClearRef.current && isEmptyHtml(message) && selectedFiles.length === 0) {
      awaitingClearRef.current = false;
    }
  }, [message, selectedFiles]);

  const handleFileChange = (event) => {
    const files = event instanceof FileList ? Array.from(event) : event;
    setSelectedFiles((prevFiles) => {
      const existing = prevFiles.map((f) => f.file.name + f.file.size);
      const uniqueFiles = files
        .filter((f) => !existing.includes(f.name + f.size))
        .map((f) => ({
          file: f,
          previewURL: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
        }));

      const updatedFiles = [...prevFiles, ...uniqueFiles];

      const filesMetadata = updatedFiles.map(({ file }) => ({
        name: file.name,
        size: file.size,
        type: file.type,
      }));

      dispatch(
        setWorkOrderDraftMessage({
          workOrderId,
          userId: selectedUser?.id,
          message,
          files: filesMetadata,
        }),
      );

      return updatedFiles;
    });
  };
  const handleRemoveFile = (index) => {
    setSelectedFiles((prevFiles) => {
      const updatedFiles = [...prevFiles];
      if (updatedFiles[index].previewURL) {
        URL.revokeObjectURL(updatedFiles[index].previewURL);
      }
      updatedFiles.splice(index, 1);

      dispatch(
        setWorkOrderDraftMessage({
          workOrderId,
          userId: selectedUser?.id,
          message,
          files: updatedFiles,
        }),
      );

      return updatedFiles;
    });
  };

  const sendVoiceMessage = async (blob, duration) => {
    if (isSendLocked()) return;

    const conversationId = getConversationId(selectedUser, chatRoom);
    if (!conversationId) return;
    isSendingRef.current = true;
    try {
      const ext = blob.type.includes("ogg") ? "ogg" : blob.type.includes("mp4") ? "m4a" : "webm";
      const audioFile = new File([blob], `voice_${Date.now()}.${ext}`, { type: blob.type });
      const formData = new FormData();
      formData.append("files", audioFile);
      formData.append("workOrderId", workOrderId);

      const filesResponse = await uploadMessageFilesMutation(formData);
      const digitalAssetIds = filesResponse.data.data;
      handleSendMessage(
        "",
        conversationId,
        workOrderId,
        selectedUser?.id,
        digitalAssetIds,
        "voice",
        null,
      );
    } catch (err) {
      console.error("Voice upload error:", err);
    } finally {
      isSendingRef.current = false;
    }
  };

  const handleDone = (tempFiles) => {
    const updatedFiles = [...selectedFiles, ...tempFiles];
    setSelectedFiles(updatedFiles);

    dispatch(
      setWorkOrderDraftMessage({
        workOrderId,
        userId: selectedUser?.id,
        message,
        files: updatedFiles,
      }),
    );
  };

  // Engineers and customers used to have exactly one room, so there was nothing
  // to list. The work order chat gives them a second one — show the list as soon
  // as there is a choice to make.
  const showChatSidebar =
    authRole === UserType.CLIENT ||
    (isCompanyOwner && chatUsersList?.length > 0) ||
    chatUsersList?.length > 1;

  // The open thread's entry in the list, which is what carries its locked state
  // and the name/logo the thread is displayed under.
  //
  // A ?engineerId= deep link opens a thread from a plain user record fetched by
  // id, so it knows nothing of the vendor the row is shown as. Matching on the
  // counterpart's id as well as on the room recovers that row - without it the
  // header named the person while the list row named their company.
  const selectedChatEntry =
    chatUsersList?.find((entry) =>
      chatRoom?.id
        ? (entry?.chatRoomId ?? entry?.conversationId) === chatRoom.id
        : entry?.id === selectedUser?.id,
    ) ??
    (selectedUser?.id && !selectedUser?.isWorkOrderChat
      ? chatUsersList?.find((entry) => entry?.id === selectedUser.id)
      : null);

  // The vendor a thread is shown under. Name and picture must come from ONE
  // row, never two: `selectedChatEntry` resolves through `chatRoom`, which lags
  // a thread switch by a render and then falls back to matching the auth user's
  // own row — pairing this company's name with that row's picture.
  //
  // `selectedUser` IS the row that was opened, so it is the truth whenever it
  // carries the company; the entry is only needed for an ?engineerId deep link,
  // where selectedUser is rebuilt by fetchUserDetails and knows nothing of it.
  const shownAsVendor = selectedUser?.freelanceCompanyName
    ? selectedUser
    : selectedChatEntry?.freelanceCompanyName
      ? selectedChatEntry
      : null;

  const selectedUserForDisplay =
    selectedChatEntry && selectedUser && selectedChatEntry.id === selectedUser.id
      ? {
          ...selectedUser,
          firstName: selectedChatEntry.firstName ?? selectedUser.firstName,
          lastName: selectedChatEntry.lastName ?? selectedUser.lastName,
          profilePicture: selectedChatEntry.profilePicture ?? selectedUser.profilePicture,
        }
      : selectedUser;

  const isChatDisabled = chatRoomSendState?.isDisabled || !!selectedChatEntry?.isDisabled;
  const chatDisabledMessage =
    chatRoomSendState?.disabledMessage ??
    selectedChatEntry?.disabledMessage ??
    "This chat has been closed. You can still read the conversation.";

  const isChatUserBlurred =
    authRole === UserType.CLIENT ? getChatUserBlurPredicate(workOrderDetail) : () => false;

  return (
    <div className="grid grid-cols-12 ">
      {showChatSidebar && (
        <div className={`lg:col-span-3 col-span-12 ${selectedUser ? "hidden lg:block" : "block"}`}>
          <ChatUsers
            isLoading={isfetchGroupChatUsersForWorkOrderApiLoading}
            chatUsers={chatUsersList}
            openChat={openChat}
            selectedUser={selectedUser}
            selectedChatRoomId={chatRoom?.id ?? null}
            unreadCount={unreadCount}
            draftMessages={draftMessages}
            workOrderId={workOrderId}
            authUserId={auth?.user?.id}
            clientCompany={workOrderDetail?.createdBy?.company}
            isChatUserBlurred={isChatUserBlurred}
          />
        </div>
      )}

      <div className={showChatSidebar ? "lg:col-span-9 col-span-12" : "col-span-12"}>
        {selectedUser && isDeletedChatUser(selectedUser) ? (
          <div className="flex flex-col items-center justify-center h-screen lg:h-[70vh] text-center px-6">
            <i className="fa-light fa-user-slash text-3xl text-gray-300 mb-3" />
            <h6 className="font-normal text-gray-700">This account has been deleted</h6>
            <p className="text-sm text-gray-400 mt-1">
              {`${selectedUser?.firstName ?? ""} ${selectedUser?.lastName ?? ""}`.trim() ||
                "This user"}{" "}
              is no longer available to chat with.
            </p>
          </div>
        ) : selectedUser ? (
          <Chat
            isLoading={!!chatRoom?.id && isMessagesLoading}
            isDisabled={isChatDisabled}
            disabledMessage={chatDisabledMessage}
            isWorkOrderChat={!!selectedChatEntry?.isWorkOrderChat}
            canManageParticipants={
              authRole === UserType.CUSTOMER &&
              (!!selectedChatEntry?.isCustomerChat || !!selectedChatEntry?.isWorkOrderChat) &&
              !!chatRoom?.id &&
              !isChatDisabled
            }
            participantCustomerId={
              authRole === UserType.CLIENT
                ? (workOrderDetail?.client?.id ?? workOrderDetail?.clientId ?? null)
                : null
            }
            participantIds={selectedChatEntry?.participantIds ?? []}
            participants={selectedChatEntry?.participants ?? []}
            onParticipantsChanged={refetchChatUsers}
            // Both off the same row: a row shown as a company carries that
            // company's logo as its picture (see chat.service).
            freelanceCompanyName={shownAsVendor?.freelanceCompanyName ?? null}
            freelanceCompanyLogo={shownAsVendor?.profilePicture ?? null}
            selectedUser={selectedUserForDisplay}
            setSelectedUser={setSelectedUser}
            sendMessage={sendMessage}
            messages={messages}
            message={message}
            lastUnreadMessageId={lastUnreadMessageId}
            loadOlderMessages={loadOlderMessages}
            hasMoreMessages={hasMoreMessages}
            isFetchingOlderMessages={isFetchingOlderMessages}
            selectedFiles={selectedFiles}
            handleDone={handleDone}
            setSelectedFiles={setSelectedFiles}
            handleMessage={(e) => handleMessage(e)}
            handleFileChange={handleFileChange}
            handleRemoveFile={handleRemoveFile}
            authUser={auth?.user}
            isCompanyOwner={isCompanyOwner}
            company={workOrderDetail?.createdBy?.company}
            isUploadMessageFilesMutationApiLoading={isUploadMessageFilesMutationApiLoading}
            showEditor={showEditor}
            setShowEditor={setShowEditor}
            workOrderId={workOrderId}
            conversationId={chatRoom?.id}
            replyTo={replyTo}
            setReplyTo={setReplyTo}
            onEditMessage={(messageId, newContent, conversationId) =>
              handleEditMessage(messageId, conversationId || chatRoom?.id, newContent)
            }
            onDeleteMessage={(messageId, conversationId) =>
              handleDeleteMessage(messageId, conversationId || chatRoom?.id)
            }
            onToggleReaction={(messageId, emoji, conversationId) =>
              handleToggleReaction(messageId, conversationId || chatRoom?.id, emoji)
            }
            onSendVoice={sendVoiceMessage}
          />
        ) : (
          <div className="flex flex-col justify-center items-center h-[500px] lg:block hidden">
            <h6 className="font-normal">No Conversation Selected</h6>
            <p>Choose a message from the list to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
});
export default ChatTab;
